import type { GuidedState } from '../../pages/GuidedWalkthroughPage';

interface Props {
  state: GuidedState;
  onChange: (updates: Partial<GuidedState>) => void;
  onNext: () => void;
  onBack: () => void;
}

interface ResourceDef {
  key: keyof GuidedState;
  icon: string;
  label: string;
  desc: string;
  multiplier: number;
  warning?: string;
}

const RESOURCES: ResourceDef[] = [
  { key: 'windowsServers', icon: '🖥️', label: 'Windows Servers', desc: 'Physical or virtual servers running Windows', multiplier: 1.0 },
  { key: 'linuxServers', icon: '🐧', label: 'Linux Servers', desc: 'Servers running Linux (Ubuntu, RHEL, etc)', multiplier: 0.5 },
  { key: 'azureVMs', icon: '☁️', label: 'Azure Virtual Machines', desc: 'VMs hosted in Microsoft Azure', multiplier: 0.1 },
  { key: 'desktops', icon: '💻', label: 'Desktops / Laptops', desc: 'End user devices with Defender for Endpoint', multiplier: 0.05 },
  {
    key: 'firewalls',
    icon: '🔥',
    label: 'Firewalls / Network Devices',
    desc: 'Hardware firewalls, switches with logging',
    multiplier: 5.0,
    warning: '⚠️ Varies significantly — firewall logs can range from 1–50 GB/day per device',
  },
  { key: 'sqlDatabases', icon: '🗄️', label: 'SQL Databases', desc: 'Microsoft SQL Server or Azure SQL', multiplier: 0.5 },
  { key: 'appServices', icon: '🌐', label: 'Azure App Services', desc: 'Web apps hosted in Azure App Service', multiplier: 0.3 },
];

export function calcDailyGB(state: GuidedState): number {
  const securityMode = state.needsSecurity || state.sentinelEnabled;

  // Per-device GB/day rates vary significantly by monitoring level.
  // Security mode: security event logging + Defender for Endpoint enabled.
  // Basic mode: performance counters and platform diagnostics only.
  const windowsRate   = securityMode ? 1.0  : 0.1;   // security events vs basic diagnostics
  const linuxRate     = securityMode ? 0.5  : 0.05;  // Syslog vs minimal
  const azureVmRate   = securityMode ? 0.1  : 0.05;  // guest OS logs vs platform metrics
  const desktopRate   = securityMode ? 0.5  : 0.01;  // Defender for Endpoint vs none
  const firewallRate  = 5.0;                          // always high — warn user
  const sqlRate       = 0.5;
  const appServiceRate = state.needsApplicationLogs ? 0.5 : 0.3;

  return (
    state.windowsServers * windowsRate +
    state.linuxServers   * linuxRate +
    state.azureVMs       * azureVmRate +
    state.desktops       * desktopRate +
    state.firewalls      * firewallRate +
    state.sqlDatabases   * sqlRate +
    state.appServices    * appServiceRate
  );
}

function Stepper({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onChange(Math.max(0, value - 1))}
        className="w-7 h-7 rounded text-[16px] flex items-center justify-center transition-colors hover:opacity-80"
        style={{ backgroundColor: 'var(--surface-container-high)', color: 'var(--text-secondary)' }}
      >−</button>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          if (!isNaN(v) && v >= 0) onChange(v);
        }}
        className="w-14 text-center rounded text-[13px] py-1 font-mono focus:outline-none focus:border-[#0078d4]"
        style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
      />
      <button
        onClick={() => onChange(value + 1)}
        className="w-7 h-7 rounded text-[16px] flex items-center justify-center transition-colors hover:opacity-80"
        style={{ backgroundColor: 'var(--surface-container-high)', color: 'var(--text-secondary)' }}
      >+</button>
    </div>
  );
}

export default function Step2Infrastructure({ state, onChange, onNext, onBack }: Props) {
  const dailyGB = calcDailyGB(state);

  const securityMode = calcDailyGB !== null && (state.needsSecurity || state.sentinelEnabled);

  const totalColour = dailyGB < 90
    ? 'text-[#0078d4]'
    : dailyGB < 200
    ? 'text-[#e8b000]'
    : 'text-[#107c10]';

  const totalHint = dailyGB < 90
    ? 'Pay-as-you-go pricing is fine at this volume'
    : dailyGB < 200
    ? 'Consider a commitment tier to save costs'
    : 'Commitment tier strongly recommended';

  return (
    <div>
      <h2 className="text-[22px] font-bold mb-1" style={{ color: 'var(--text-primary)' }}>What does your IT environment look like?</h2>
      <p className="text-[14px] mb-8" style={{ color: 'var(--text-tertiary)' }}>
        We use these numbers to estimate how much data your monitoring will generate.
      </p>

      <div className="flex flex-col gap-3 mb-6">
        {RESOURCES.map((r) => {
          const val = state[r.key] as number;
          const gbPerDay = val * r.multiplier;
          return (
            <div key={r.key} className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <span className="text-[22px] leading-none mt-0.5 shrink-0">{r.icon}</span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold" style={{ color: 'var(--text-secondary)' }}>{r.label}</p>
                    <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{r.desc}</p>
                    {r.warning && (
                      <p className="text-[11px] text-[#e8b000] mt-1">{r.warning}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Stepper
                    value={val}
                    onChange={(v) => onChange({ [r.key]: v } as Partial<GuidedState>)}
                  />
                  <span className="text-[11px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
                    ~{gbPerDay.toFixed(1)} GB/day
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Live total */}
      <div className="bg-[#0078d4]/10 border border-[#0078d4]/30 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[12px] mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Estimated daily ingestion</p>
            <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{totalHint}</p>
            <p className="text-[11px] mt-1">
              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                securityMode
                  ? 'bg-[#0078d4]/20 text-[#0078d4]'
                  : ''
              }`}>
                {securityMode ? '🔒 Security mode rates' : '📊 Basic monitoring rates'}
              </span>
              <span className="ml-1.5" style={{ color: 'var(--text-tertiary)' }}>— based on your Step 3 choices</span>
            </p>
          </div>
          <p className={`text-[28px] font-bold font-mono ${totalColour}`}>
            {dailyGB.toFixed(1)} <span className="text-[16px]">GB/day</span>
          </p>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="px-5 py-3 rounded-lg text-[14px] transition-all flex items-center gap-2"
          style={{ border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Back
        </button>
        <button
          onClick={onNext}
          className="flex-1 py-3 rounded-lg font-semibold text-[14px] bg-[#0078d4] hover:bg-[#106ebe] text-white transition-all flex items-center justify-center gap-2"
        >
          Next: Monitoring Needs
          <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
        </button>
      </div>
    </div>
  );
}
